import type { PublicUser } from '@evi/shared';
import { TODAY_PATH, landsOnToday } from './todayInbox';

/**
 * MFA flow plumbing (commit 4 of the admin-MFA arc) — the client
 * side of the pending-token contract, dormant until commit 5 arms
 * the server.
 *
 * THE CONTRACT (commit 5 emits exactly this): when MFA applies,
 * login and magic-link verify return — INSTEAD of a session —
 *
 *   { mfa_required: true,
 *     variant: 'enroll' | 'challenge',
 *     pending_token: '<5-minute evi-mfa-pending JWT>' }
 *
 * The consumers detect the shape via isMfaPending, stash the token
 * in the in-memory flow state below, and route to the matching
 * screen. The screens exchange the token (plus a TOTP or backup
 * code) for a real session at the /auth/mfa endpoints, and login
 * completes exactly as it does today.
 */

export interface MfaPendingResponse {
  mfa_required: true;
  variant: 'enroll' | 'challenge';
  pending_token: string;
}

/** Narrow an auth response to the MFA-pending shape. Anything that
 *  doesn't match exactly falls through to the normal session path,
 *  so a server that never emits the shape (today's server) leaves
 *  the existing flow untouched. */
export function isMfaPending(data: unknown): data is MfaPendingResponse {
  if (typeof data !== 'object' || data === null) return false;
  const d = data as Record<string, unknown>;
  return (
    d['mfa_required'] === true
    && typeof d['pending_token'] === 'string'
    && (d['variant'] === 'enroll' || d['variant'] === 'challenge')
  );
}

// ── In-memory flow state ─────────────────────────────────────────────
// Deliberately NOT persisted (no localStorage, no URL): the pending
// token lives five minutes and exists only to bridge two screens in
// one tab. A refresh mid-flow loses it, and the MFA pages bounce
// back to /login — re-entering primary auth is the correct recovery
// for a token that short-lived.

let flow: { pendingToken: string; variant: 'enroll' | 'challenge'; next?: string } | null = null;

/** `next` is where the finished sign-in lands instead of the role's usual page: an in-app path only, set by our own
 *  screens (the Sales Partner invite sends a new partner straight to New job, Warren, 21 October 2026). */
export function setMfaFlow(pending: MfaPendingResponse, opts: { next?: string } = {}): void {
  const next = opts.next && /^\/[a-z0-9/_-]*(\?[a-z0-9=&_-]*)?$/i.test(opts.next) && !opts.next.startsWith('//') ? opts.next : undefined;
  flow = { pendingToken: pending.pending_token, variant: pending.variant, ...(next ? { next } : {}) };
}

export function getMfaFlow(): { pendingToken: string; variant: 'enroll' | 'challenge'; next?: string } | null {
  return flow;
}

/** Where a new Sales Partner lands once the invite's authenticator is set up and the terms are accepted (Warren, 21
 *  October 2026): the New job screen, with its first-visit welcome. */
export const FIRST_JOB_PATH = '/sales-jobs/new?first=1';

/** Where a completed sign-in lands: the flow's own next page when it has one, else the role's landing page. */
export function finishedSignInPath(user: Pick<PublicUser, 'role' | 'template'>, next?: string): string {
  return next ?? landingPathFor(user);
}

export function clearMfaFlow(): void {
  flow = null;
}

/** Role-aware post-login landing — the same rule MagicLinkVerifyPage
 *  and App.tsx apply: fleet_admin → /fleets, building_admin
 *  (template-flagged) → /reports, everyone else → /dashboard. The
 *  MFA screens complete a login, so they land identically. */
export function landingPathFor(user: Pick<PublicUser, 'role' | 'template'>): string {
  if (user.role === 'fleet_admin') return '/fleets';
  // Warren, 21 October 2026, item M: the Super Admin starts on the Today inbox.
  if (landsOnToday(user)) return TODAY_PATH;
  // Warren, 21 October 2026: SP Managers and Sales Managers start on Today, a partner's teammate on its Jobs; the
  // partner owner's home (/dashboard) opens with one big "Start a new job".
  if (user.template === 'sales_rep' || user.template === 'sales_manager') return '/sales-today';
  if (user.template === 'sales_partner_member') return '/sales-jobs';
  if (user.template === 'building_admin') return '/reports';
  return '/dashboard';
}
